import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ViewChild,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import {
  FieldType,
  FieldTypeConfig,
  FormlyFieldConfig,
  FormlyModule,
} from '@ngx-formly/core';
import {
  FormlyFieldSelectProps,
  FormlySelectModule,
} from '@ngx-formly/core/select';
import { FormlyFieldProps } from '@ngx-formly/primeng/form-field';
import {
  Dropdown,
  DropdownFilterEvent,
  DropdownLazyLoadEvent,
  DropdownModule,
} from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { SkeletonModule } from 'primeng/skeleton';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  debounceTime,
  firstValueFrom,
  skip,
} from 'rxjs';

interface SelectLazyProps extends FormlyFieldProps, FormlyFieldSelectProps {
  query: (
    parPage: number,
    page: number,
    search?: string,
    filters?: string,
  ) => Observable<{ data: unknown[]; meta: { total: number; to: number } }>;
  pageSize?: number;
  filterPlaceholder?: string;
  getValueCondition?: (value: any) => string;
  initValueField?: string;
  mapLabel?: (item: any) => string;
  afterViewInit?: (component: FormlySelectLazyTypeComponent) => any;
}

export interface FormlySelectLazyFieldConfig
  extends FormlyFieldConfig<SelectLazyProps> {}

@Component({
  selector: 'kon-select-lazy-type',
  standalone: true,
  imports: [
    CommonModule,
    FormlyModule,
    DropdownModule,
    FormlySelectModule,
    ReactiveFormsModule,
    InputTextModule,
    SkeletonModule,
  ],
  template: `
    <p-dropdown
      #select
      [id]="id"
      [appendTo]="'body'"
      [options]="(items | formlySelectOptions : field | async) ?? []"
      [optionLabel]="'label'"
      [optionValue]="'value'"
      [formControl]="formControl"
      [showClear]="!props.required"
      (onChange)="props.change && props.change(field, $event)"
      [virtualScroll]="true"
      [filter]="true"
      [autofocusFilter]="true"
      [resetFilterOnHide]="true"
      [style]="{
        width: '100%',
        height: 'var(--input-height)',
        'min-height': '42px',
      }"
      [lazy]="true"
      [virtualScroll]="true"
      [virtualScrollItemSize]="42"
      (onLazyLoad)="onLazyLoad($event)"
      [virtualScrollOptions]="{
        step: props.pageSize ?? 50,
        showLoader: true
      }"
      (onShow)="onShow()"
      (onHide)="onHide()"
      (onFilter)="onSearch($event)"
      [filterMatchMode]="$any('lazy')"
      [placeholder]="props.filterPlaceholder ?? ''"
    >
      <ng-template pTemplate="loader" let-options="options">
        <div
          class="flex items-center px-3"
          style="height: var(--input-height);"
        >
          <p-skeleton
            class="w-full"
            [width]="options.even ? '60%' : '50%'"
            height="1rem"
          ></p-skeleton>
        </div>
      </ng-template>
    </p-dropdown>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlySelectLazyTypeComponent
  extends FieldType<FieldTypeConfig<SelectLazyProps>>
  implements AfterViewInit
{
  @ViewChild('select') select?: Dropdown;

  items = new BehaviorSubject<unknown[]>([]);

  searchDebounce = new BehaviorSubject<string>('');
  search = '';

  searchSubscription?: Subscription;

  ngAfterViewInit(): void {
    if (this.select?.filterService) {
      // this disables default filtering
      this.select.filterService.register('lazy', () => true);
    }
    if (this.select?.value) {
      this.freshLoad(undefined, this.select.value);
    }
    if (this.props.afterViewInit) {
      this.props.afterViewInit(this);
    }
  }

  async freshLoad(search = '', itemValue: unknown = undefined) {
    this.select?.scroller?.scrollToIndex(0);
    this.search = search;
    if (itemValue && this.props.initValueField) {
      // option by mal uz byt na modeli (napr. ako relation)
      const initValue = this.model[this.props.initValueField];
      if (this.props.mapLabel) {
        const labelProp = this.getLabelProp(initValue);
        initValue[labelProp] = this.props.mapLabel(initValue);
      }
      this.patchItems(0, [initValue], 1);
      return;
    }
    const filter = this.getCurrentValueFilter(itemValue);

    const response = await firstValueFrom(
      this.props.query(this.pageSize(), 1, search, filter),
    );
    this.patchItems(0, response.data, response.meta.total);
    this.select?.focusedOptionIndex.set(0); // focus first item
  }

  async onLazyLoad(event: DropdownLazyLoadEvent) {
    const first = Math.max(event.first, 0); // prevent negative first
    const page = Math.floor(first / this.pageSize()) + 1;

    const response = await firstValueFrom(
      this.props.query(this.pageSize(), page, this.search),
    );

    const includesCurrentValue =
      !this.select?.value ||
      response.data.some(
        (i: any) => i?.[this.props.valueProp as string] === this.select?.value,
      );

    let currentValueOptions: unknown[] = [];

    if (this.select?.value && !includesCurrentValue) {
      currentValueOptions = await this.getCurrentValueOptions(
        this.select?.value,
      );
    }
    const itemsToPatch = [...response.data, ...currentValueOptions];

    const total = Math.max(response.meta.total, itemsToPatch.length);
    this.patchItems(first, itemsToPatch, total);
  }

  async getCurrentValueOptions(itemValue: unknown = undefined) {
    const filter = this.getCurrentValueFilter(itemValue);
    const response = await firstValueFrom(
      this.props.query(this.pageSize(), 1, '', filter),
    );
    return response.data;
  }

  private getCurrentValueFilter(itemValue?: unknown) {
    return itemValue
      ? this.props.getValueCondition
        ? this.props.getValueCondition(itemValue)
        : `${this.props.valueProp} eq "${itemValue}"`
      : undefined;
  }

  onSearch(event: DropdownFilterEvent) {
    if (event.filter === this.searchDebounce.value) return;
    this.searchDebounce.next(event.filter);
  }

  patchItems(start: number, items: unknown[], total: number) {
    let newItems = [...this.items.value];

    if (newItems.length != total) {
      newItems = Array.from({ length: total }, function () {
        return {};
      });
    }
    let idx = start;
    items.forEach((item) => {
      newItems[idx] = item;
      idx++;
    });
    this.items.next(newItems);
  }

  onShow() {
    this.freshLoad();
    this.searchSubscription = this.searchDebounce
      .pipe(skip(1), debounceTime(500))
      .subscribe((search) => {
        this.freshLoad(search);
      });
  }

  onHide() {
    this.searchSubscription?.unsubscribe();
    this.searchDebounce.next('');
  }

  pageSize = () => this.props.pageSize ?? 50;

  private getLabelProp(option: any) {
    if (this.props.labelProp) {
      if (typeof this.props.labelProp === 'function') {
        return this.props.labelProp(option);
      } else {
        return this.props.labelProp;
      }
    }
    return 'label';
  }
}
